import { appPath } from "../appPath";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import batchStyles from "../batch/batch.css?inline";
import financialAnalysisStyles from "../financial-analysis/styles/module.css?inline";
import financialReportStyles from "../financial-analysis/styles/report.css?inline";
import { ApiError, adjustFirmAccountQuota, bindLostContactMember, cancelLostContactOutboundProfileAction, createFirmAccount, createLostContactOutboundProfile, createLostContactRepairBatch, customerLogout, deleteLostContactOutboundProfile, disableLostContactOutboundProfile, enableLostContactOutboundProfile, getCustomerProducts, getCustomerSession, getInquiryResult, getLostContactRepairBatch, getReportExport, getSupport, getWallet, listFirmAccounts, listLostContactOutboundProfiles, listLostContactRepairBatches, listManagedLostContactOutboundProfiles, listOrders, listSupport, lookupLostContactMemberContacts, payCustomerOrderFromBalance, previewCustomerOrder, replySupport, request, requestReportExport, resetFirmAccountPassword, saveLostContactBindingContactRecord, saveReportExportUrl, setCsrfToken, setCustomerSessionInvalidHandler, submitLostContactRepairBatch, submitSupport, unbindLostContactMember, updateFirmAccount, updateLostContactOutboundProfile, uploadLostContactRepairBatch, uploadOrderAuthorizationMaterial, validateLostContactRepairUpload } from "../api/client";
import type { LostContactOutcome } from "../api/client";
import { LawFirmAccountCenter } from "../customer/LawFirmAccountCenter";
import { PersonalLawyerCenter } from "../customer-personal/PersonalLawyerCenter";
import { PaymentChoice } from "../customer/PaymentChoice";
import { BatchWorkbench } from "../batch/BatchWorkbench";
import { FinancialAnalysisModule, PlatformFinancialAnalysisService } from "../financial-analysis";
import type { CreateOrderInput, CustomerIdentity, CustomerOrganizationType, CustomerProductCatalog, QueryOrder, ReportExportSummary, WalletSummary as Wallet } from "../types/api";
import { buildInstitutionWorkspaceContext, parseInstitutionAccountCommand, runInstitutionAccountCommand } from "./institutionMessageBridge";
import { buildLawFirmAccountContext, buildOrderContextMessage, buildProductContextMessage, resolveResultRequest } from "./messageBridge";

type Area = "services" | "accounts" | "batch" | "personal-lawyer";
const financialAnalysisService = new PlatformFinancialAnalysisService(request);

function validId(value: unknown): value is string {
  return typeof value === "string" && /^[0-9a-f-]{36}$/i.test(value);
}

function sameReportExport(left: ReportExportSummary | null | undefined, right: ReportExportSummary): boolean {
  return left?.id === right.id
    && left.status === right.status
    && left.filename === right.filename
    && left.resultNumber === right.resultNumber
    && left.resultVersion === right.resultVersion
    && left.canSave === right.canSave
    && left.errorSummary === right.errorSummary;
}

const LOST_CONTACT_OUTCOMES = new Set<LostContactOutcome>([
  "PENDING", "CONNECTED", "NO_ANSWER", "REJECTED", "INVALID_NUMBER", "OTHER",
]);

export function lostContactErrorMessage(reason: unknown) {
  const errorCode = reason instanceof ApiError ? reason.code : undefined;
  return {
    type: "institution:lost-contact-error",
    message: reason instanceof Error ? reason.message : "失联修复操作失败，请稍后重试",
    ...(errorCode ? { code: errorCode } : {}),
  };
}

export function parseLostContactRecordCommand(value: Record<string, unknown>): {
  bindingId: string;
  outcome: LostContactOutcome;
  note: string;
} | null {
  const bindingId = value.bindingId;
  const outcome = value.outcome;
  const note = value.note;
  if (!validId(bindingId) || typeof outcome !== "string" || !LOST_CONTACT_OUTCOMES.has(outcome as LostContactOutcome) || typeof note !== "string") return null;
  const trimmedNote = note.trim();
  if (trimmedNote.length > 500) return null;
  return { bindingId, outcome: outcome as LostContactOutcome, note: trimmedNote };
}

function validRepairRows(value: unknown): Array<{ name: string; idCard: string; phone?: string }> | null {
  if (!Array.isArray(value) || value.length === 0 || value.length > 1000) return null;
  const rows = value.map((raw) => {
    if (!raw || typeof raw !== "object") return null;
    const item = raw as Record<string, unknown>;
    const name = typeof item.name === "string" ? item.name.trim() : "";
    const idCard = typeof item.idCard === "string" ? item.idCard.trim() : "";
    const phone = typeof item.phone === "string" ? item.phone.trim() : "";
    return name && idCard ? { name, idCard, ...(phone ? { phone } : {}) } : null;
  });
  return rows.every((row): row is { name: string; idCard: string; phone?: string } => row !== null)
    ? rows
    : null;
}

function validUpload(value: unknown): value is File {
  if (!value || typeof value !== "object") return false;
  const file = value as { name?: unknown; size?: unknown; arrayBuffer?: unknown };
  return typeof file.name === "string"
    && typeof file.size === "number"
    && typeof file.arrayBuffer === "function";
}

function validOutboundProfile(value: unknown): { name: string; seatNumber: string; telX: string; tag1: string | null; tag2: string | null; active: boolean } | null {
  if (!value || typeof value !== "object") return null;
  const raw = value as Record<string, unknown>;
  const string = (key: string) => typeof raw[key] === "string" ? raw[key].trim() : "";
  const name = string("name"), seatNumber = string("seatNumber"), telX = string("telX");
  if (!name || !seatNumber || !telX || typeof raw.active !== "boolean") return null;
  return { name, seatNumber, telX, tag1: string("tag1") || null, tag2: string("tag2") || null, active: raw.active };
}

export function accountCenterReturnArea(): Area {
  return "services";
}

export function customerDestination(type: CustomerOrganizationType) {
  return type === "LAW_FIRM" ? "LAW_FIRM_PORTAL" : "STANDARD_INSTITUTION_PORTAL";
}

function SessionExpiredDialog({ onRelogin }: { onRelogin: () => void }) {
  return <div className="customer-payment-modal" role="dialog" aria-modal="true" aria-labelledby="session-expired-title"><section className="customer-card"><h2 id="session-expired-title">登录已失效</h2><p>登录状态已失效，请重新登录。</p><p>本次操作未提交。</p><button onClick={onRelogin}>重新登录</button></section></div>;
}

export function PortalApp() {
  const [identity, setIdentity] = useState<CustomerIdentity | null | undefined>(undefined);
  const [area, setArea] = useState<Area>("services");
  const [batchHost, setBatchHost] = useState<HTMLElement | null>(null);
  const [batchOpened, setBatchOpened] = useState(false);
  const [financialAnalysisHost, setFinancialAnalysisHost] = useState<HTMLElement | null>(null);
  const [financialAnalysisOpened, setFinancialAnalysisOpened] = useState(false);
  const [wallet, setWallet] = useState<Wallet | null>(null);
  const [orders, setOrders] = useState<QueryOrder[]>([]);
  const [products, setProducts] = useState<CustomerProductCatalog>({ modules: [] });
  const [pendingOrder, setPendingOrder] = useState<{
    input: CreateOrderInput;
    salePrice: number;
  } | null>(null);
  const [error, setError] = useState("");
  const [sessionExpired, setSessionExpired] = useState(false);
  const pendingReportExportOrderIds = orders
    .filter((order) => ["QUEUED", "RUNNING"].includes(order.reportExport?.status ?? ""))
    .map((order) => order.id)
    .sort()
    .join(",");
  useEffect(() => {
    setCustomerSessionInvalidHandler(() => {
      setCsrfToken(null);
      setPendingOrder(null);
      setSessionExpired(true);
    });
    return () => setCustomerSessionInvalidHandler(null);
  }, []);
  useEffect(() => {
    void getCustomerSession().then(setIdentity).catch(() => setIdentity(null));
  }, []);
  useEffect(() => {
    if (identity === null && !sessionExpired) window.location.replace(appPath("/?login=1"));
  }, [identity, sessionExpired]);
  useEffect(() => {
    if (!identity) return;
    void getWallet().then(setWallet);
    void listOrders().then(setOrders);
  }, [identity]);
  useEffect(() => {
    if (!identity) return;
    let active = true;
    const refreshProducts = () => {
      void getCustomerProducts().then((catalog) => {
        if (active) setProducts(catalog);
      });
    };
    refreshProducts();
    const refreshWhenFocused = () => {
      if (document.visibilityState === "visible") refreshProducts();
    };
    window.addEventListener("focus", refreshProducts);
    document.addEventListener("visibilitychange", refreshWhenFocused);
    return () => {
      active = false;
      window.removeEventListener("focus", refreshProducts);
      document.removeEventListener("visibilitychange", refreshWhenFocused);
    };
  }, [identity]);
  useEffect(() => {
    if (!identity || area !== "services") return;
    const target = document.querySelector<HTMLIFrameElement>(".portal-frame")?.contentWindow;
    if (customerDestination(identity.organization_type) === "STANDARD_INSTITUTION_PORTAL") {
      if (wallet) target?.postMessage(
        buildInstitutionWorkspaceContext(identity, wallet, products),
        window.location.origin,
      );
      target?.postMessage(buildOrderContextMessage(orders), window.location.origin);
      return;
    }
    target?.postMessage(
      buildOrderContextMessage(orders),
      window.location.origin,
    );
    target?.postMessage(
      buildProductContextMessage(products, identity.authorization_material_required, identity.organization_name),
      window.location.origin,
    );
    if (wallet) target?.postMessage(
      buildLawFirmAccountContext(identity, wallet),
      window.location.origin,
    );
  }, [area, identity, orders, products, wallet]);
  useEffect(() => {
    if (!identity || !pendingReportExportOrderIds) return;
    const orderIds = pendingReportExportOrderIds.split(",");
    const knownExports = new Map(orders
      .filter((order) => order.reportExport && orderIds.includes(order.id))
      .map((order) => [order.id, order.reportExport!]));
    const started = Date.now();
    let timeout: number | undefined;
    let stopped = false;
    const refresh = async () => {
      if (stopped) return;
      const results = await Promise.allSettled(orderIds.map(async (orderId) => ({
        orderId,
        reportExport: await getReportExport(orderId),
      })));
      if (stopped) return;
      let keepPolling = false;
      const updates = results.flatMap((result) => {
        if (result.status === "rejected") {
          keepPolling = true;
          return [];
        }
        const { orderId, reportExport } = result.value;
        if (["QUEUED", "RUNNING"].includes(reportExport.status)) keepPolling = true;
        if (sameReportExport(knownExports.get(orderId), reportExport)) return [];
        knownExports.set(orderId, reportExport);
        return [{ orderId, reportExport }];
      });
      if (updates.length) {
        setOrders((existing) => existing.map((order) => {
          const update = updates.find((candidate) => candidate.orderId === order.id);
          return update ? { ...order, reportExport: update.reportExport } : order;
        }));
        const target = document.querySelector<HTMLIFrameElement>(".portal-frame")?.contentWindow;
        updates.forEach(({ orderId, reportExport }) => target?.postMessage(
          { type: "judicial:report-export-context", orderId, reportExport },
          window.location.origin,
        ));
      }
      if (keepPolling) timeout = window.setTimeout(refresh, Date.now() - started < 120_000 ? 3_000 : 10_000);
    };
    timeout = window.setTimeout(refresh, 3_000);
    return () => { stopped = true; if (timeout) window.clearTimeout(timeout); };
  }, [identity, pendingReportExportOrderIds]);
  useEffect(() => {
    if (!identity) return;
    const receiveOrder = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const frame = document.querySelector<HTMLIFrameElement>(".portal-frame");
      const source = frame?.contentWindow;
      const fromPortalFrame = source === event.source;
      const command = event.data as Record<string, unknown> | null;
      const respondLostContact = (message: object) => source?.postMessage(message, window.location.origin);
      const rejectLostContact = (reason: unknown) => respondLostContact(lostContactErrorMessage(reason));
      if (fromPortalFrame && command?.type === "judicial:request-report-export" && validId(command.orderId)) {
        void requestReportExport(command.orderId)
          .then((reportExport) => {
            setOrders((existing) => existing.map((order) => order.id === command.orderId ? { ...order, reportExport } : order));
            source?.postMessage({ type: "judicial:report-export-context", orderId: command.orderId, reportExport }, window.location.origin);
          })
          .catch((reason: Error) => source?.postMessage({ type: "judicial:report-export-failed", message: reason.message }, window.location.origin));
        return;
      }
      if (fromPortalFrame && command?.type === "judicial:get-report-export" && validId(command.orderId)) {
        void getReportExport(command.orderId)
          .then((reportExport) => source?.postMessage({ type: "judicial:report-export-context", orderId: command.orderId, reportExport }, window.location.origin))
          .catch(() => source?.postMessage({ type: "judicial:report-export-context", orderId: command.orderId, reportExport: null }, window.location.origin));
        return;
      }
      if (fromPortalFrame && command?.type === "judicial:save-report-export" && validId(command.orderId)) {
        source?.postMessage({ type: "judicial:save-report-export-context", orderId: command.orderId, url: saveReportExportUrl(command.orderId) }, window.location.origin);
        return;
      }
      if (fromPortalFrame && command?.type === "judicial:refresh-orders") {
        void Promise.all([listOrders(), getWallet()])
          .then(([nextOrders, nextWallet]) => {
            setOrders(nextOrders);
            setWallet(nextWallet);
            source?.postMessage(buildOrderContextMessage(nextOrders), window.location.origin);
            if (identity.organization_type === "LAW_FIRM") source?.postMessage(
              buildLawFirmAccountContext(identity, nextWallet),
              window.location.origin,
            );
          })
          .catch((reason: Error) => source?.postMessage({
            type: "judicial:orders-refresh-failed",
            message: reason.message || "订单刷新失败，请稍后重试",
          }, window.location.origin));
        return;
      }
      if (fromPortalFrame && command?.type === "institution:lost-contact-load") {
        void Promise.all([listLostContactRepairBatches(), listLostContactOutboundProfiles()])
          .then(([batches, profiles]) => respondLostContact({ type: "institution:lost-contact-context", batches, profiles: profiles.items, canManageOutboundProfiles: identity.role === "ORGANIZATION_ADMIN" }))
          .catch(rejectLostContact);
        return;
      }
      if (fromPortalFrame && command?.type === "institution:lost-contact-load-batch" && validId(command.batchId)) {
        void getLostContactRepairBatch(command.batchId)
          .then((batch) => respondLostContact({ type: "institution:lost-contact-batch", batch }))
          .catch(rejectLostContact);
        return;
      }
      if (fromPortalFrame && command?.type === "institution:lost-contact-load-record-batch" && validId(command.batchId)) {
        void getLostContactRepairBatch(command.batchId)
          .then((batch) => respondLostContact({ type: "institution:lost-contact-record-batch", batch }))
          .catch(rejectLostContact);
        return;
      }
      if (fromPortalFrame && command?.type === "institution:lost-contact-create") {
        const rows = validRepairRows(command.rows);
        const idempotencyKey = typeof command.idempotencyKey === "string" ? command.idempotencyKey.trim() : "";
        if (!rows || !idempotencyKey) {
          rejectLostContact(new Error("名单或幂等键格式不正确"));
          return;
        }
        void createLostContactRepairBatch(rows, idempotencyKey)
          .then((batch) => respondLostContact({ type: "institution:lost-contact-created", batch }))
          .catch(rejectLostContact);
        return;
      }
      if (fromPortalFrame && command?.type === "institution:lost-contact-upload") {
        const file = command.file;
        const idempotencyKey = typeof command.idempotencyKey === "string" ? command.idempotencyKey.trim() : "";
        if (!validUpload(file) || !idempotencyKey) {
          rejectLostContact(new Error("上传文件或幂等键格式不正确"));
          return;
        }
        void uploadLostContactRepairBatch(file, idempotencyKey)
          .then((batch) => respondLostContact({ type: "institution:lost-contact-created", batch }))
          .catch(rejectLostContact);
        return;
      }
      if (fromPortalFrame && command?.type === "institution:lost-contact-validate-upload") {
        const file = command.file;
        if (!validUpload(file)) {
          rejectLostContact(new Error("上传文件格式不正确"));
          return;
        }
        void validateLostContactRepairUpload(file)
          .then((validation) => respondLostContact({ type: "institution:lost-contact-upload-validation", validation }))
          .catch(rejectLostContact);
        return;
      }
      if (fromPortalFrame && command?.type === "institution:lost-contact-load-profiles-manage") {
        void listManagedLostContactOutboundProfiles()
          .then((profiles) => respondLostContact({ type: "institution:lost-contact-profiles-managed", profiles: profiles.items }))
          .catch(rejectLostContact);
        return;
      }
      if (fromPortalFrame && command?.type === "institution:lost-contact-create-profile") {
        const profile = validOutboundProfile(command.profile);
        if (!profile) { rejectLostContact(new Error("外呼资源字段不正确")); return; }
        void createLostContactOutboundProfile(profile)
          .then((created) => respondLostContact({ type: "institution:lost-contact-profile-saved", profile: created }))
          .catch(rejectLostContact);
        return;
      }
      if (fromPortalFrame && command?.type === "institution:lost-contact-update-profile" && validId(command.profileId)) {
        const profile = validOutboundProfile(command.profile);
        if (!profile) { rejectLostContact(new Error("外呼资源字段不正确")); return; }
        void updateLostContactOutboundProfile(command.profileId, profile)
          .then((updated) => respondLostContact({ type: "institution:lost-contact-profile-saved", profile: updated }))
          .catch(rejectLostContact);
        return;
      }
      if (fromPortalFrame && command?.type === "institution:lost-contact-disable-profile" && validId(command.profileId)) {
        void disableLostContactOutboundProfile(command.profileId)
          .then((updated) => respondLostContact({ type: "institution:lost-contact-profile-saved", profile: updated }))
          .catch(rejectLostContact);
        return;
      }
      if (fromPortalFrame && command?.type === "institution:lost-contact-enable-profile" && validId(command.profileId)) {
        void enableLostContactOutboundProfile(command.profileId)
          .then((updated) => respondLostContact({ type: "institution:lost-contact-profile-saved", profile: updated }))
          .catch(rejectLostContact);
        return;
      }
      if (fromPortalFrame && command?.type === "institution:lost-contact-cancel-profile-action" && validId(command.profileId)) {
        void cancelLostContactOutboundProfileAction(command.profileId)
          .then((updated) => respondLostContact({ type: "institution:lost-contact-profile-saved", profile: updated }))
          .catch(rejectLostContact);
        return;
      }
      if (fromPortalFrame && command?.type === "institution:lost-contact-delete-profile" && validId(command.profileId)) {
        void deleteLostContactOutboundProfile(command.profileId)
          .then((outcome) => respondLostContact({ type: "institution:lost-contact-profile-deleted", ...outcome, profileId: command.profileId }))
          .catch(rejectLostContact);
        return;
      }
      if (fromPortalFrame && command?.type === "institution:lost-contact-submit" && validId(command.batchId)) {
        void submitLostContactRepairBatch(command.batchId)
          .then((batch) => respondLostContact({ type: "institution:lost-contact-submitted", batch }))
          .catch(rejectLostContact);
        return;
      }
      if (fromPortalFrame && command?.type === "institution:lost-contact-contacts" && validId(command.memberId)) {
        void lookupLostContactMemberContacts(command.memberId)
          .then((result) => respondLostContact({ type: "institution:lost-contact-contacts", memberId: command.memberId, ...result }))
          .catch(rejectLostContact);
        return;
      }
      if (fromPortalFrame && command?.type === "institution:lost-contact-bind" && validId(command.memberId) && validId(command.outboundProfileId) && Number.isInteger(command.contactIndex) && Number(command.contactIndex) >= 0) {
        void bindLostContactMember(command.memberId, command.outboundProfileId, Number(command.contactIndex))
          .then((binding) => respondLostContact({ type: "institution:lost-contact-bound", memberId: command.memberId, binding }))
          .catch(rejectLostContact);
        return;
      }
      if (fromPortalFrame && command?.type === "institution:lost-contact-unbind" && validId(command.bindingId)) {
        void unbindLostContactMember(command.bindingId)
          .then((binding) => respondLostContact({ type: "institution:lost-contact-unbound", binding }))
          .catch(rejectLostContact);
        return;
      }
      if (fromPortalFrame && command?.type === "institution:lost-contact-save-contact-record") {
        const contactRecord = parseLostContactRecordCommand(command);
        if (!contactRecord) {
          rejectLostContact(new Error("联系标记或备注格式不正确"));
          return;
        }
        void saveLostContactBindingContactRecord(
          contactRecord.bindingId,
          contactRecord.outcome,
          contactRecord.note,
        )
          .then((binding) => respondLostContact({
            type: "institution:lost-contact-contact-record-saved",
            bindingId: contactRecord.bindingId,
            binding,
          }))
          .catch(rejectLostContact);
        return;
      }
      if (event.data?.type === "institution:open-batch") {
        if (event.source === frame?.contentWindow && identity.organization_type !== "LAW_FIRM") setBatchOpened(true);
        return;
      }
      if (event.data?.type === "institution:open-financial-analysis") {
        const frame = document.querySelector<HTMLIFrameElement>(".portal-frame");
        if (event.source === frame?.contentWindow && identity.organization_type !== "LAW_FIRM") setFinancialAnalysisOpened(true);
        return;
      }
      if (event.data?.type === "institution:logout") {
        const frame = document.querySelector<HTMLIFrameElement>(".portal-frame");
        if (event.source === frame?.contentWindow && identity.organization_type !== "LAW_FIRM") void logout();
        return;
      }
      const institutionCommand = parseInstitutionAccountCommand(event.data);
      if (institutionCommand && customerDestination(identity.organization_type) === "STANDARD_INSTITUTION_PORTAL") {
        const source = document.querySelector<HTMLIFrameElement>(".portal-frame")?.contentWindow;
        if (identity.role !== "ORGANIZATION_ADMIN") {
          source?.postMessage({ type: "institution:account-action-failed", message: "仅机构管理员可以管理子账号" }, window.location.origin);
          return;
        }
        void runInstitutionAccountCommand(institutionCommand, {
          listAccounts: listFirmAccounts,
          getWallet,
          createAccount: createFirmAccount,
          adjustQuota: adjustFirmAccountQuota,
          setStatus: updateFirmAccount,
          resetPassword: resetFirmAccountPassword,
        }).then((messages) => {
          messages.forEach((message) => source?.postMessage(message, window.location.origin));
        }).catch((reason: Error) => source?.postMessage({
          type: "institution:account-action-failed",
          message: reason.message,
        }, window.location.origin));
        return;
      }
      if (event.data?.type === "judicial:open-firm-accounts" && identity.role === "ORGANIZATION_ADMIN") {
        setArea("accounts");
        return;
      }
      if (event.data?.type === "judicial:open-personal-center" && identity.is_personal_lawyer) {
        setArea("personal-lawyer");
        return;
      }
      if (event.data?.type === "judicial:open-support") {
        const source = document.querySelector<HTMLIFrameElement>(".portal-frame")?.contentWindow;
        source?.postMessage({ type: "judicial:support-context", orders }, window.location.origin);
        void listSupport().then((tickets) => source?.postMessage({ type: "judicial:support-list", tickets }, window.location.origin));
        return;
      }
      if (event.data?.type === "judicial:submit-support") {
        const source = document.querySelector<HTMLIFrameElement>(".portal-frame")?.contentWindow;
        void submitSupport(event.data.orderId, event.data.question, Array.isArray(event.data.images) ? event.data.images : [])
          .then(() => listSupport())
          .then((tickets) => source?.postMessage({ type: "judicial:support-submitted", tickets }, window.location.origin))
          .catch((reason: Error) => source?.postMessage({ type: "judicial:support-failed", message: reason.message }, window.location.origin));
        return;
      }
      if (event.data?.type === "judicial:list-support") {
        const source = document.querySelector<HTMLIFrameElement>(".portal-frame")?.contentWindow;
        void listSupport().then((tickets) => source?.postMessage({ type: "judicial:support-list", tickets }, window.location.origin)).catch((reason: Error) => source?.postMessage({ type: "judicial:support-failed", message: reason.message }, window.location.origin));
        return;
      }
      if (event.data?.type === "judicial:open-support-detail") {
        const source = document.querySelector<HTMLIFrameElement>(".portal-frame")?.contentWindow;
        void getSupport(event.data.ticketId).then((ticket) => source?.postMessage({ type: "judicial:support-detail", ticket }, window.location.origin)).catch((reason: Error) => source?.postMessage({ type: "judicial:support-failed", message: reason.message }, window.location.origin));
        return;
      }
      if (event.data?.type === "judicial:reply-support") {
        const source = document.querySelector<HTMLIFrameElement>(".portal-frame")?.contentWindow;
        void replySupport(event.data.ticketId, event.data.body, Array.isArray(event.data.images) ? event.data.images : []).then((ticket) => source?.postMessage({ type: "judicial:support-detail", ticket }, window.location.origin)).then(() => listSupport()).then((tickets) => source?.postMessage({ type: "judicial:support-list", tickets }, window.location.origin)).catch((reason: Error) => source?.postMessage({ type: "judicial:support-failed", message: reason.message }, window.location.origin));
        return;
      }
      if (event.data?.type === "judicial:logout") {
        void logout();
        return;
      }
      if (event.data?.type === "judicial:open-order-result") {
        const source = document.querySelector<HTMLIFrameElement>(".portal-frame")?.contentWindow;
        void resolveResultRequest(event.data, getInquiryResult)
          .then((message) => {
            if (message) source?.postMessage(message, window.location.origin);
          })
          .catch((reason: Error) => source?.postMessage({
            type: "judicial:order-result-failed",
            message: reason.message,
          }, window.location.origin));
        return;
      }
      if (!fromPortalFrame || command?.type !== "judicial:create-order") return;
      setError("");
      const authorizationFile = validUpload(command.authorizationFile)
        ? command.authorizationFile
        : null;
      if (identity.authorization_material_required && !authorizationFile) {
        const message = "请先选择授权材料。";
        setError(message);
        source?.postMessage({ type: "judicial:order-preview-failed", message }, window.location.origin);
        return;
      }
      const input: CreateOrderInput = {
        productCode: command.productCode as CreateOrderInput["productCode"],
        subjectSummary: String(command.subjectSummary ?? ""),
        inquiryPayload: command.inquiryPayload as CreateOrderInput["inquiryPayload"],
      };
      const quote = authorizationFile
        ? uploadOrderAuthorizationMaterial(authorizationFile)
          .then((material) => previewCustomerOrder({
            ...input,
            authorizationMaterialIds: [material.id],
          }).then((quote) => ({ input: { ...input, authorizationMaterialIds: [material.id] }, quote })))
        : previewCustomerOrder(input).then((quote) => ({ input, quote }));
      void quote
        .then(({ input: pendingInput, quote }) => {
          setPendingOrder({ input: pendingInput, salePrice: quote.salePrice });
          source?.postMessage({ type: "judicial:order-quote-ready" }, window.location.origin);
        })
        .catch((reason: Error) => {
          const message = reason.message || "获取报价失败，请稍后重试";
          setError(message);
          source?.postMessage({ type: "judicial:order-preview-failed", message }, window.location.origin);
        });
    };
    window.addEventListener("message", receiveOrder);
    return () => window.removeEventListener("message", receiveOrder);
  }, [identity, orders]);
  function returnToLogin() {
    setCsrfToken(null);
    window.location.assign(appPath("/?login=1"));
  }
  if (identity === undefined) return <div className="session-loading">正在进入服务…</div>;
  if (identity === null) return sessionExpired
    ? <SessionExpiredDialog onRelogin={returnToLogin} />
    : <div className="session-loading">正在返回统一入口…</div>;
  async function paymentFinished() {
    setPendingOrder(null);
    try {
      const [nextWallet, nextOrders] = await Promise.all([getWallet(), listOrders()]);
      setWallet(nextWallet);
      setOrders(nextOrders);
      const target = document.querySelector<HTMLIFrameElement>(".portal-frame")?.contentWindow;
      target?.postMessage(buildOrderContextMessage(nextOrders), window.location.origin);
      if (identity?.organization_type === "LAW_FIRM") target?.postMessage(
        buildLawFirmAccountContext(identity, nextWallet),
        window.location.origin,
      );
      target?.postMessage({ type: "judicial:order-paid" }, window.location.origin);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "支付成功，查询记录刷新失败，请重新进入查询记录");
    }
  }
  async function payPendingOrder() {
    if (!pendingOrder) return;
    await payCustomerOrderFromBalance(pendingOrder.input);
    await paymentFinished();
  }
  async function logout() {
    try {
      await customerLogout();
      setCsrfToken(null);
      window.location.assign(appPath("/?login=1"));
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "退出失败，请重试");
    }
  }
  const destination = customerDestination(identity.organization_type);
  const prototypeRole = identity.role === "ORGANIZATION_ADMIN" ? "FIRM_ADMIN" : identity.role;
  const frameSrc = destination === "LAW_FIRM_PORTAL"
    ? `/prototype/index.html?role=${prototypeRole}`
    : `/prototype/institution-workbench.html?role=${identity.role}`;
  const frameTitle = destination === "LAW_FIRM_PORTAL" ? "司法主体协查服务" : "通用机构客户工作台";
  return <div className="customer-shell">{area === "services" && <iframe className="portal-frame portal-frame--full" src={appPath(frameSrc)} title={frameTitle} onLoad={(event) => {
    if (destination === "STANDARD_INSTITUTION_PORTAL") {
      setBatchHost(event.currentTarget.contentDocument?.getElementById("view-batch") ?? null);
      setFinancialAnalysisHost(event.currentTarget.contentDocument?.getElementById("view-financial-analysis") ?? null);
      if (wallet) event.currentTarget.contentWindow?.postMessage(
        buildInstitutionWorkspaceContext(identity, wallet, products),
        window.location.origin,
      );
      event.currentTarget.contentWindow?.postMessage(buildOrderContextMessage(orders), window.location.origin);
      return;
    }
    event.currentTarget.contentWindow?.postMessage(buildOrderContextMessage(orders), window.location.origin);
    event.currentTarget.contentWindow?.postMessage(
      buildProductContextMessage(products, identity.authorization_material_required, identity.organization_name),
      window.location.origin,
    );
    if (wallet) event.currentTarget.contentWindow?.postMessage(
      buildLawFirmAccountContext(identity, wallet),
      window.location.origin,
    );
  }} />}{batchHost && batchOpened && createPortal(<><style>{batchStyles}</style><BatchWorkbench identity={identity} /></>, batchHost)}{financialAnalysisHost && financialAnalysisOpened && createPortal(<><style>{financialAnalysisStyles}</style><style>{financialReportStyles}</style><FinancialAnalysisModule service={financialAnalysisService} /></>, financialAnalysisHost)}{area === "accounts" && wallet && <LawFirmAccountCenter wallet={wallet} role={identity.role} onHome={() => setArea(accountCenterReturnArea())} onQuotaChanged={() => void getWallet().then(setWallet)} />}{area === "personal-lawyer" && identity.is_personal_lawyer && <PersonalLawyerCenter onHome={() => setArea("services")} />}{sessionExpired ? <SessionExpiredDialog onRelogin={returnToLogin} /> : error && <div className="customer-payment-modal"><section className="customer-card"><p className="form-error">{error}</p><button onClick={() => setError("")}>关闭</button></section></div>}{!sessionExpired && pendingOrder && wallet && <div className="customer-payment-modal"><PaymentChoice firmBalance={wallet.firm_balance} lawyerQuota={wallet.lawyer_quota} unallocatedBalance={wallet.unallocated_balance} role={identity.role} orderAmount={pendingOrder.salePrice} onPay={payPendingOrder} onClose={() => setPendingOrder(null)} /></div>}</div>;
}
